import * as PIXI from 'pixi.js'
import { TransformerHandle } from '@pixi-essentials/transformer'
import Transformer, { MinimumScaleType } from '@/pixi/Transformer'
import { CssColours } from '@/static/colours'
import { destroyTransformerAxisLines } from '@/pixi/snapPositioning'
import { DisplayObjectPair } from '@/pixi/types'
import { useApplicationStore } from '@/store/application-store'

export interface TransformerHandleDataInterface {
  isTransformerHandleGrabbed: boolean
  selectedHandle: TransformerHandle | null
}

export interface MouseFlagsInterface {
  isMouseDown: boolean
}

export default function useTransformer () {
  const createTransformer = (
    spriteGroup: PIXI.DisplayObject[],
    minScaleType: MinimumScaleType,
    application: PIXI.Application,
    transformerHandleData: TransformerHandleDataInterface,
    mouseFlags: MouseFlagsInterface,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): Transformer => {
    const transformer = new Transformer({ group: spriteGroup }, application.screen, minScaleType)
    transformer.interactive = true
    transformer.lockAspectRatio = true
    transformer.rotateEnabled = false

    // Set size of the scale handles and border
    transformer.scale.set(3)

    // Style the handles and border
    transformer.handleStyle = { outlineColor: PIXI.utils.string2hex(CssColours.INTERACTIVE_BLUE), outlineThickness: 2 }
    transformer.wireframeStyle = { color: PIXI.utils.string2hex(CssColours.INTERACTIVE_BLUE), thickness: 2 }

    Transformer.ScaleHandles.forEach((handle) => {
      const transformerHandle = transformer.getHandle(handle)

      if (transformerHandle) {
        transformerHandle.addListener('pointerdown', () => {
          transformerHandleData.selectedHandle = transformerHandle
          mouseFlags.isMouseDown = true
          transformerHandleData.isTransformerHandleGrabbed = true
        })
        transformerHandle.addListener('pointerup', () => {
          transformerHandleData.selectedHandle = null
          mouseFlags.isMouseDown = false
          transformerHandleData.isTransformerHandleGrabbed = false
        })
        transformerHandle.addListener('pointerupoutside', () => {
          transformerHandleData.selectedHandle = null
          mouseFlags.isMouseDown = false
          transformerHandleData.isTransformerHandleGrabbed = false
        })

        // Needed to prevent scaling using right click
        transformerHandle.addListener('rightdown', () => {
          if (transformerHandleData.selectedHandle) {
            transformerHandleData.selectedHandle.emit('pointerup')
          }
        })
      }
    })

    // Some general pointer up listeners for overlapping layers
    transformer.addListener('pointerup', () => {
      if (mouseFlags.isMouseDown) {
        mouseFlags.isMouseDown = false
        emitPointerUpOnSelectedEntity(textDisplayObjectsMap, stickerDisplayObjectsMap)
      }
      if (transformerHandleData.selectedHandle) {
        transformerHandleData.selectedHandle.emit('pointerup')
      }
      setTimeout(() => destroyTransformerAxisLines(), 100)
    })

    transformer.addListener('pointerupoutside', () => {
      mouseFlags.isMouseDown = false
      setTimeout(() => destroyTransformerAxisLines(), 100)
    })

    return transformer
  }

  /**
   * This method emits the pointer up event on the dragged selected entity, so that it wont become sticky when the
   * user releases their pointer in the area of another entity
   */
  const emitPointerUpOnSelectedEntity = (
    textDisplayObjectsMap: Map<number, DisplayObjectPair>,
    stickerDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): void => {
    const applicationStore = useApplicationStore()
    let selectedTransformer = null
    if (applicationStore.selectedText) {
      selectedTransformer = textDisplayObjectsMap.get(applicationStore.selectedText?.id)?.transformer
    } else if (applicationStore.selectedSticker) {
      selectedTransformer = stickerDisplayObjectsMap.get(applicationStore.selectedSticker?.id)?.transformer
    }

    if (selectedTransformer) {
      const event = new PIXI.InteractionEvent()
      event.currentTarget = selectedTransformer
      selectedTransformer.emit('pointerup', event)
    }
  }

  return {
    createTransformer
  }
}
